import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { parseAsInteger, useQueryState } from 'nuqs'
import React, { Suspense, useState } from 'react'

import AddOrUpdateCommentModal from '@/components/misc/Comments/components/AddOrUpdateCommentModal'
import Comment from '@/components/misc/Comments/components/Comment'
import CommentVisibilityNote from '@/components/misc/Comments/components/CommentVisibilityNote'
import {
  NUM_RESULTS_PER_PAGE,
  SEARCH_PARAMS,
} from '@/components/misc/Comments/constants'
import { useInvalidateCommentQueries } from '@/components/misc/Comments/hooks/useInvalidateCommentQueries'
import SkeletonBox from '@/components/skeleton/SkeletonBox'
import { Button } from '@/components/ui/button'
import { Pagination } from '@/components/ui/pagination'
import useDialog from '@/hooks/useDialog'
import useNotyf from '@/hooks/useNotyf'
import { useShowGenericServerError } from '@/hooks/useShowGenericServerError'
import { faPlus } from '@/lib/fontAwesomePro/regular'
import { scrollToTop } from '@/lib/helpers/helperFunctions'
import { QUERY_KEYS } from '@/lib/queryKeys'
import commentService from '@/lib/services/commentService'
import { CommentEntityAlias } from '@/types/misc'
import { GetCommentsRequestParams } from '@/types/requests/comments'
import { CommentCollectionItem } from '@/types/responses/comments'

interface Props {
  entityAlias: CommentEntityAlias
  entityId: number
}

const CommentListContent: React.FC<Props> = ({ entityAlias, entityId }) => {
  const addNoteDialog = useDialog()
  const invalidateCommentQueries = useInvalidateCommentQueries()
  const notyf = useNotyf()
  const showGenericServerError = useShowGenericServerError()
  const [page, setPage] = useQueryState(
    SEARCH_PARAMS.commentsPage,
    parseAsInteger.withDefault(1),
  )

  const [commentBeingEdited, setCommentBeingEdited] =
    useState<CommentCollectionItem | null>(null)

  const params: GetCommentsRequestParams = {
    entityAlias: entityAlias,
    entityId,
    page,
    pageSize: NUM_RESULTS_PER_PAGE,
  }

  const commentsQuery = useSuspenseQuery({
    queryFn: () => commentService.getComments(params),
    queryKey: QUERY_KEYS.comments(params),
    staleTime: 20000,
  })

  const comments = commentsQuery.data['hydra:member']
  const totalItems = commentsQuery.data['hydra:totalItems']

  async function resetPage() {
    await setPage(1)
    scrollToTop()
  }

  const togglePinStatusMutation = useMutation({
    mutationFn: (comment: CommentCollectionItem) =>
      commentService.togglePinStatus(comment.id),
    onSuccess: async (_response, comment) => {
      const hasJustPinned = !comment.isPinned

      if (hasJustPinned) {
        await resetPage()
      }

      await invalidateCommentQueries()
      notyf.success(
        `Comment successfully ${comment.isPinned ? 'unpinned' : 'pinned'}`,
      )
    },
    onError: showGenericServerError,
    retry: false,
  })

  const deleteMutation = useMutation({
    mutationFn: (comment: CommentCollectionItem) =>
      commentService.deleteComment(comment.id),
    onSuccess: async () => {
      const hasDeletedOnlyCommentOnPage = comments.length === 1

      if (page > 1 && hasDeletedOnlyCommentOnPage) {
        await setPage((currentPage) => currentPage - 1)
      }

      await invalidateCommentQueries()
      notyf.success('Comment successfully deleted')
    },
    onError: showGenericServerError,
    retry: false,
  })

  return (
    <>
      <CommentVisibilityNote />

      {totalItems === 0 && (
        <p className="mb-2">You haven't added any comments yet.</p>
      )}

      <Button
        size="lg"
        variant="success"
        className="mb-4 flex flex-row items-center gap-2"
        onClick={addNoteDialog.showDialog}
      >
        <FontAwesomeIcon icon={faPlus} />
        Add a comment
      </Button>

      {comments.map((comment) => (
        <Comment
          key={comment.id}
          comment={comment}
          isDeleting={deleteMutation.isPending}
          onPin={() => togglePinStatusMutation.mutate(comment)}
          isPinning={togglePinStatusMutation.isPending}
          onEdit={() => setCommentBeingEdited(comment)}
          onDelete={() => {
            const hasConfirmedDelete = window.confirm(
              'Are you sure? This action cannot be undone',
            )

            if (!hasConfirmedDelete) {
              return
            }

            deleteMutation.mutate(comment)
          }}
        />
      ))}

      {totalItems > NUM_RESULTS_PER_PAGE && (
        <div className="mt-4">
          <Pagination
            activePage={page}
            totalItemsCount={totalItems}
            numItemsPerPage={NUM_RESULTS_PER_PAGE}
            onChange={async (newPage) => {
              await setPage(newPage)
              scrollToTop()
            }}
          />
        </div>
      )}

      {addNoteDialog.isShowingDialog && (
        <AddOrUpdateCommentModal
          entity={entityAlias}
          entityId={entityId}
          onHide={addNoteDialog.hideDialog}
          onAdd={resetPage} // Reset page to show the newly added comment
        />
      )}

      {commentBeingEdited && (
        <AddOrUpdateCommentModal
          entity={entityAlias}
          entityId={entityId}
          onHide={() => setCommentBeingEdited(null)}
          comment={commentBeingEdited}
        />
      )}
    </>
  )
}

const CommentList: React.FC<Props> = (props) => (
  <>
    <Suspense fallback={<CommentListSkeleton />}>
      <CommentListContent {...props} />
    </Suspense>
  </>
)

const CommentListSkeleton: React.FC = () => (
  <div className="flex flex-col gap-4">
    {new Array(3).fill('x').map((_, index) => (
      <SkeletonBox key={index} style={{ height: '140px' }} />
    ))}
  </div>
)

export default CommentList
